import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = '/team/international-hero.jpg';

const COPY = {
  pt: {
    seoTitle: 'International Buyers · Comprar Imóvel em Portugal | Your Broker Portugal',
    seoDescription:
      'Consultoria privada para compradores internacionais em Portugal. Representamos exclusivamente clientes dos EUA, Reino Unido, Brasil e União Europeia na aquisição de imobiliário premium em Lisboa, Cascais, Algarve e Comporta. NIF, Golden Visa, due diligence cross-border e negociação independente.',
    eyebrow: 'International Buyers',
    heroTitle: 'Comprar em Portugal,',
    heroTitleEm: 'representado com discrição.',
    heroSub:
      'Trabalhamos exclusivamente com compradores internacionais. Acompanhamos a sua aquisição em Portugal com a profundidade que clientes em Nova Iorque, Londres ou São Paulo esperam encontrar no seu private banker, no seu fiscalista, no seu advogado.',
    heroCta: 'Iniciar conversa privada',
    heroCtaSec: 'O nosso processo',

    profilesEyebrow: 'Os clientes que representamos',
    profilesTitle: 'Quatro perfis. Uma única abordagem.',
    profilesIntro:
      'Não há um comprador internacional típico. Há objetivos distintos — fiscalidade, qualidade de vida, diversificação, herança familiar. O denominador comum é a procura de critério onde há ruído.',
    profiles: [
      {
        flag: 'US',
        title: 'Compradores norte-americanos',
        body: 'Famílias e indivíduos que procuram Portugal por estilo de vida, segurança e fiscalidade favorável. Coordenamos com escritórios fiscais transatlânticos (FBAR/FATCA), bancos privados e equipas legais que entendem trusts e LLCs.',
      },
      {
        flag: 'UK',
        title: 'Compradores britânicos pós-Brexit',
        body: 'Acompanhamento de aquisição residencial ou de segunda habitação, com clareza sobre o regime de não-residente, NHR (regime sucessor) e estruturas patrimoniais. Tradição de discrição britânica preservada em todos os pontos do processo.',
      },
      {
        flag: 'BR',
        title: 'Compradores brasileiros',
        body: 'Fala portuguesa, mas mercado distinto. Apoiamos a transferência de capital via instituições autorizadas, abertura de NIF, escolha entre Lisboa, Porto e Algarve, e a compreensão do enquadramento legal português face ao brasileiro.',
      },
      {
        flag: 'EU',
        title: 'Compradores europeus continentais',
        body: 'Alemães, franceses, suíços, escandinavos e holandeses. Investidores experientes que valorizam mercado transparente, processo discreto e acesso a oportunidades fora dos canais públicos.',
      },
    ],

    processEyebrow: 'O processo cross-border',
    processTitle: 'Discreto. Estruturado. Sem surpresas.',
    processSub:
      'Cada cliente internacional segue o mesmo arco de seis etapas. A simplicidade da estrutura permite que cada decisão receba a atenção que merece.',

    realitiesEyebrow: 'Realidades técnicas',
    realitiesTitle: 'O que realmente precisa de saber.',
    realitiesSub:
      'Antes da decisão sentimental, há uma decisão administrativa. Resumimos abaixo os seis tópicos que mais perguntas geram. Esclarecimento adicional caso a caso, sem custo.',
    realities: [
      { label: 'NIF', title: 'Número de Identificação Fiscal', body: 'Obrigatório para qualquer transação imobiliária. Obtemos o seu via representante fiscal qualificado. Prazo: 5 a 10 dias úteis.' },
      { label: 'D7 Visa', title: 'Residência por rendimento passivo', body: 'Para quem comprova rendimentos recorrentes (pensões, dividendos, aluguer). Permite residir em Portugal. Tipicamente 6–9 meses até decisão.' },
      { label: 'D8 Visa', title: 'Residência para trabalhadores remotos', body: 'Para profissionais com contrato ou clientes fora de Portugal. Mínimo €3 480/mês. Atrai um perfil cada vez mais comum.' },
      { label: 'Golden Visa', title: 'Programa atualizado pós-2023', body: 'A compra direta de imóveis foi removida em outubro de 2023. Permanece via fundos de investimento qualificados (mín. €500K). Análise caso a caso.' },
      { label: 'IMT', title: 'Imposto sobre transmissões', body: 'Escalonado de 0% a 7,5% conforme valor de aquisição e finalidade (habitação própria vs investimento). Pago antes da escritura.' },
      { label: 'NHR', title: 'Regime fiscal sucessor (IFICI)', body: 'O antigo Non-Habitual Resident foi substituído pelo "IFICI" em 2024 — para atividades de alto valor acrescentado. Elegibilidade muito mais restrita.' },
    ],
    process: [
      {
        num: '01',
        label: 'Briefing privado',
        body: 'Conversa inicial, frequentemente por vídeo, em português ou inglês. Compreensão de objetivos, prazos, perfil fiscal e o que verdadeiramente importa.',
      },
      {
        num: '02',
        label: 'NIF e estrutura legal',
        body: 'Obtemos o seu Número de Identificação Fiscal português via representante fiscal. Avaliamos estrutura ideal (singular, sociedade, fundo) com aconselhamento legal independente.',
      },
      {
        num: '03',
        label: 'Curadoria silenciosa',
        body: 'Apresentamos três a quatro oportunidades. Imóveis on-market filtrados com critério, mais acesso à nossa rede off-market. Sem catálogos. Sem fadiga.',
      },
      {
        num: '04',
        label: 'Visitas coordenadas',
        body: 'Agendamento eficiente das visitas presenciais. Para clientes que não conseguem viajar, conduzimos visitas em vídeo com nível de detalhe imobiliário, técnico e de bairro.',
      },
      {
        num: '05',
        label: 'Negociação independente',
        body: 'Representamos sempre o comprador. Negociamos preço, condições, contingências e calendário com o vendedor ou a mediadora vendedora. Sem comissões cruzadas.',
      },
      {
        num: '06',
        label: 'Due diligence e fecho',
        body: 'Coordenamos advogado independente, técnico, banco e notário. Apoiamos transferência internacional de capital. Acompanhamos cada etapa até à entrega de chaves.',
      },
    ],

    expertiseEyebrow: 'Áreas de competência',
    expertiseTitle: 'A complexidade tratada nos bastidores.',
    expertise: [
      { title: 'NIF e representação fiscal', body: 'Obtenção, manutenção e descontinuação quando aplicável.' },
      { title: 'Golden Visa (programa atualizado)', body: 'Análise de elegibilidade nos fundos de investimento qualificados, atual desde a revisão de 2023.' },
      { title: 'Regime fiscal não-habitual / sucessor', body: 'Avaliação de elegibilidade e impacto fiscal real ao longo de 10 anos.' },
      { title: 'Transferência internacional de capital', body: 'Coordenação com instituições autorizadas e compliance KYC/AML.' },
      { title: 'Compra através de sociedade', body: 'Quando faz sentido fiscal, sucessório ou de privacidade — e quando não faz.' },
      { title: 'Due diligence técnica', body: 'Engenharia, registo, urbanismo, condomínio, encargos ocultos.' },
      { title: 'Crédito imobiliário a não-residentes', body: 'Apresentação a bancos privados e instituições especializadas.' },
      { title: 'Coordenação com advogados independentes', body: 'Nunca trabalhamos com o "advogado do vendedor". Independência absoluta.' },
    ],

    faqEyebrow: 'Perguntas frequentes',
    faqTitle: 'O que os clientes internacionais perguntam.',
    faq: [
      {
        q: 'Preciso de estar em Portugal para comprar?',
        a: 'Não. Toda a transação pode ser conduzida remotamente, com procuração ao seu advogado e visitas em vídeo. Recomendamos pelo menos uma visita antes da assinatura do CPCV (contrato-promessa).',
      },
      {
        q: 'Qual o custo total para além do preço de venda?',
        a: 'Em Portugal: IMT (imposto de transmissão, escalonado 0–7,5%), Imposto do Selo (0,8%), honorários notariais e registo (~1–1,5%), e advogado independente (~0,8–1,2%). Total típico para o comprador: 3–9% do valor da compra. A Your Broker Portugal não cobra honorários ao comprador — a nossa remuneração sai da comissão de mercado paga pelo vendedor.',
      },
      {
        q: 'Trabalham apenas com propriedades de luxo?',
        a: 'Trabalhamos com clientes para quem cada aquisição é significativa. O ticket médio dos nossos mandatos situa-se entre €700.000 e €4M, mas o critério não é o preço — é o ajuste cliente-projeto.',
      },
      {
        q: 'Como são remunerados? O comprador paga alguma coisa?',
        a: 'O comprador não nos paga nada. A nossa remuneração sai da comissão de mercado paga pela parte vendedora — estrutura padrão do imobiliário em Portugal. O comprador suporta apenas os custos legais inerentes a qualquer aquisição: IMT, Imposto do Selo, escritura, registo e advogado independente. A nossa independência vem da clareza do mandato exclusivo de representação do comprador, não de quem assina o cheque.',
      },
      {
        q: 'Em que línguas trabalham?',
        a: 'Português e inglês — fluentes em ambos. Estas são as nossas línguas de trabalho diárias com clientes.',
      },
      {
        q: 'O Golden Visa ainda existe para imobiliário?',
        a: 'A compra direta de imóveis foi removida do programa em outubro de 2023. Permanece via fundos de investimento qualificados. Esclarecemos elegibilidade caso a caso.',
      },
    ],

    closingTitle: 'A sua aquisição em Portugal começa por uma conversa.',
    closingBody:
      'Trinta minutos, em vídeo, sem agenda comercial. Compreendemos o seu projeto e indicamos honestamente se somos o parceiro certo.',
    closingCta: 'Iniciar conversa privada',
  },

  en: {
    seoTitle: 'International Buyers · Buying Property in Portugal | Your Broker Portugal',
    seoDescription:
      'Private advisory for international buyers in Portugal. We represent clients from the US, UK, Brazil and EU exclusively in acquiring premium real estate in Lisbon, Cascais, Algarve and Comporta. NIF, Golden Visa, cross-border due diligence and independent negotiation.',
    eyebrow: 'International Buyers',
    heroTitle: 'Buying in Portugal,',
    heroTitleEm: 'represented with discretion.',
    heroSub:
      'We work exclusively with international buyers. We accompany your acquisition in Portugal with the depth that clients in New York, London or São Paulo expect from their private banker, their tax advisor, their lawyer.',
    heroCta: 'Begin a private conversation',
    heroCtaSec: 'Our process',

    profilesEyebrow: 'The clients we represent',
    profilesTitle: 'Four profiles. One single approach.',
    profilesIntro:
      'There is no typical international buyer. There are distinct objectives — taxation, quality of life, diversification, family legacy. The common denominator is the search for criteria where there is noise.',
    profiles: [
      {
        flag: 'US',
        title: 'North American buyers',
        body: 'Families and individuals seeking Portugal for lifestyle, security and favourable taxation. We coordinate with transatlantic tax offices (FBAR/FATCA), private banks and legal teams familiar with trusts and LLCs.',
      },
      {
        flag: 'UK',
        title: 'Post-Brexit British buyers',
        body: 'Residential or second-home acquisitions, with clarity on non-resident status, NHR (successor regime) and patrimonial structures. The British tradition of discretion preserved at every step.',
      },
      {
        flag: 'BR',
        title: 'Brazilian buyers',
        body: 'Shared language, distinct market. We support capital transfer via authorised institutions, NIF setup, the choice between Lisbon, Porto and Algarve, and the navigation of Portuguese legal frameworks vis-à-vis Brazilian.',
      },
      {
        flag: 'EU',
        title: 'Continental European buyers',
        body: 'German, French, Swiss, Scandinavian and Dutch. Experienced investors who value a transparent market, a discreet process and access to opportunities beyond the public channel.',
      },
    ],

    processEyebrow: 'The cross-border process',
    processTitle: 'Discreet. Structured. Without surprises.',
    processSub:
      'Every international client follows the same six-step arc. The simplicity of the structure means each decision receives the attention it deserves.',

    realitiesEyebrow: 'Technical realities',
    realitiesTitle: 'What you actually need to know.',
    realitiesSub:
      'Before the sentimental decision, there is an administrative one. We summarise below the six topics that generate the most questions. Further clarification on a case-by-case basis, at no cost.',
    realities: [
      { label: 'NIF', title: 'Portuguese Tax ID', body: 'Mandatory for any property transaction. We obtain yours via a qualified fiscal representative. Lead time: 5 to 10 business days.' },
      { label: 'D7 Visa', title: 'Passive income residency', body: 'For those with recurring income (pensions, dividends, rental). Allows residency in Portugal. Typically 6–9 months until decision.' },
      { label: 'D8 Visa', title: 'Remote worker residency', body: 'For professionals with contracts or clients outside Portugal. Minimum €3,480/month. Increasingly common profile.' },
      { label: 'Golden Visa', title: 'Updated post-2023 programme', body: 'Direct property purchase was removed in October 2023. Remains accessible via qualified investment funds (min. €500K). Case-by-case analysis.' },
      { label: 'IMT', title: 'Property transfer tax', body: 'Tiered from 0% to 7.5% depending on purchase value and purpose (primary home vs investment). Paid before the deed.' },
      { label: 'NHR', title: 'Successor tax regime (IFICI)', body: 'The former Non-Habitual Resident was replaced by "IFICI" in 2024 — for high-value-added activities. Significantly stricter eligibility.' },
    ],
    process: [
      {
        num: '01',
        label: 'Private briefing',
        body: 'Initial conversation, usually by video, in Portuguese or English. Understanding objectives, timing, tax profile and what truly matters.',
      },
      {
        num: '02',
        label: 'NIF and legal structure',
        body: 'We obtain your Portuguese Tax Identification Number via a fiscal representative. We assess the optimal structure (individual, company, fund) with independent legal counsel.',
      },
      {
        num: '03',
        label: 'Quiet curation',
        body: 'We present three to four opportunities. On-market filtered with criteria, plus access to our off-market network. No catalogues. No fatigue.',
      },
      {
        num: '04',
        label: 'Coordinated visits',
        body: 'Efficient scheduling of on-site visits. For clients unable to travel, we conduct video visits with the detail of property, technical condition and neighbourhood.',
      },
      {
        num: '05',
        label: 'Independent negotiation',
        body: 'We always represent the buyer. We negotiate price, conditions, contingencies and timeline with the seller or selling agency. No cross-commissions.',
      },
      {
        num: '06',
        label: 'Due diligence and closing',
        body: 'We coordinate independent lawyer, technical expert, bank and notary. We support international capital transfer. We accompany every step through to handover.',
      },
    ],

    expertiseEyebrow: 'Areas of expertise',
    expertiseTitle: 'Complexity handled behind the scenes.',
    expertise: [
      { title: 'NIF and fiscal representation', body: 'Setup, maintenance and discontinuation when applicable.' },
      { title: 'Golden Visa (updated programme)', body: 'Eligibility analysis in qualified investment funds, current as of the 2023 revision.' },
      { title: 'Non-habitual / successor tax regime', body: 'Eligibility assessment and real fiscal impact over 10 years.' },
      { title: 'International capital transfer', body: 'Coordination with authorised institutions and KYC/AML compliance.' },
      { title: 'Acquisition through a company', body: 'When it makes fiscal, succession or privacy sense — and when it does not.' },
      { title: 'Technical due diligence', body: 'Engineering, registry, urbanism, condominium, hidden encumbrances.' },
      { title: 'Non-resident mortgages', body: 'Introductions to private banks and specialised institutions.' },
      { title: 'Independent legal coordination', body: 'We never work with the "seller\'s lawyer". Absolute independence.' },
    ],

    faqEyebrow: 'Frequently asked questions',
    faqTitle: 'What international clients ask.',
    faq: [
      {
        q: 'Do I need to be in Portugal to buy?',
        a: 'No. The entire transaction can be conducted remotely, with power of attorney to your lawyer and video visits. We recommend at least one visit before signing the CPCV (promissory contract).',
      },
      {
        q: 'What is the total cost beyond the sale price?',
        a: 'In Portugal: IMT (transfer tax, tiered 0–7.5%), Stamp Duty (0.8%), notary and registry (~1–1.5%) and independent lawyer (~0.8–1.2%). Typical total for the buyer: 3–9% of the purchase value. Your Broker Portugal charges no fees to the buyer — our compensation comes from the market commission paid by the seller.',
      },
      {
        q: 'Do you only work with luxury properties?',
        a: 'We work with clients for whom each acquisition is significant. Average mandate sits between €700,000 and €4M, but the criterion is not price — it is client-project fit.',
      },
      {
        q: 'How are you paid? Does the buyer pay anything?',
        a: 'The buyer pays us nothing. Our compensation comes from the market commission paid by the seller side — the standard structure of Portuguese real estate. The buyer only bears the legal costs inherent to any acquisition: IMT, Stamp Duty, deed, registry and independent lawyer. Our independence comes from the clarity of the exclusive buyer-representation mandate, not from who signs the cheque.',
      },
      {
        q: 'In which languages do you work?',
        a: 'Portuguese and English — fluent in both. These are our daily working languages with clients.',
      },
      {
        q: 'Does Golden Visa still exist for real estate?',
        a: 'Direct property purchase was removed from the programme in October 2023. It remains accessible via qualified investment funds. We clarify eligibility on a case-by-case basis.',
      },
    ],

    closingTitle: 'Your acquisition in Portugal begins with a conversation.',
    closingBody:
      'Thirty minutes, by video, with no commercial agenda. We understand your project and tell you honestly whether we are the right partner.',
    closingCta: 'Begin a private conversation',
  },
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-visible'), io.unobserve(e.target))),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const InternationalPage = () => {
  const { language, localizedPath } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const ref = useReveal();
  const [homeContent, setHomeContent] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/home-content`).then((r) => setHomeContent(r.data)).catch(() => {});
  }, []);

  // Schema.org FAQ structured data for SEO
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: copy.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'International Real Estate Buying Agent Portugal',
      provider: { '@type': 'RealEstateAgent', name: 'Your Broker Portugal' },
      areaServed: { '@type': 'Country', name: 'Portugal' },
      audience: { '@type': 'Audience', audienceType: 'International real estate buyers' },
      description: copy.seoDescription,
    };
    let tag = document.getElementById('intl-schema');
    if (!tag) {
      tag = document.createElement('script');
      tag.id = 'intl-schema';
      tag.type = 'application/ld+json';
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify([faqSchema, serviceSchema]);
    return () => {
      const t = document.getElementById('intl-schema');
      if (t) t.remove();
    };
  }, [language, copy]);

  return (
    <>
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords="buying agent Portugal, international buyer Portugal, comprar imóvel Portugal estrangeiro, Golden Visa Portugal, NIF Portugal, NHR Portugal, real estate Portugal foreigners, cross-border property Portugal, US buyer Portugal, UK buyer Portugal, Brazilian buyer Portugal, German buyer Portugal"
        url="https://yourbrokerportugal.com/international"
      />
      <style>{`
        body { background: #f7f4ef; }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity .95s ease, transform .95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
        @keyframes intlHeroZoom { from { transform: scale(1.07); } to { transform: scale(1); } }
        @keyframes intlFadeUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <Header
        companyName={homeContent?.headerFooter?.companyName || 'Your Broker Portugal'}
        logo={homeContent?.headerFooter?.logo}
      />

      <main ref={ref}>
        {/* HERO */}
        <section className="relative h-[88vh] min-h-[600px] flex items-end overflow-hidden" data-testid="intl-hero">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${HERO_IMAGE})`,
              backgroundPosition: 'center 35%',
              animation: 'intlHeroZoom 14s ease-out forwards',
            }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(18,16,12,0.88) 0%, rgba(18,16,12,0.32) 55%, rgba(18,16,12,0.18) 100%)' }}
          />
          <div className="relative z-10 px-6 md:px-10 lg:px-16 xl:px-24 pb-20 md:pb-28 max-w-[860px]">
            <div className="opacity-0" style={{ animation: 'intlFadeUp 1s 0.3s ease forwards' }}>
              <Eyebrow dark>{copy.eyebrow}</Eyebrow>
            </div>
            <h1
              className="opacity-0 text-editorial-bone font-light mt-7"
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2.6rem, 5vw, 4.6rem)',
                lineHeight: '1.05',
                animation: 'intlFadeUp 1s 0.55s ease forwards',
              }}
            >
              {copy.heroTitle}{' '}
              <em className="italic text-editorial-sand-deep">{copy.heroTitleEm}</em>
            </h1>
            <p
              className="opacity-0 text-editorial-bone/60 mt-7 max-w-[560px] font-light"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.95rem',
                lineHeight: '1.92',
                animation: 'intlFadeUp 1s 0.8s ease forwards',
              }}
            >
              {copy.heroSub}
            </p>
            <div
              className="opacity-0 mt-10 flex flex-col sm:flex-row gap-6 sm:items-center"
              style={{ animation: 'intlFadeUp 1s 1s ease forwards' }}
            >
              <a
                href={buildWhatsAppUrl('international', language)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10 font-medium"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                }}
                data-testid="intl-hero-cta-whatsapp"
              >
                {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
              </a>
              <Link
                to={localizedPath('/contact')}
                className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase' }}
                data-testid="intl-hero-cta"
              >
                {copy.heroCta}
              </Link>
            </div>
          </div>
        </section>

        {/* PROFILES */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-mineral">
          <div className="max-w-6xl mx-auto">
            <div className="rv max-w-3xl">
              <Eyebrow>{copy.profilesEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.profilesTitle}
              </h2>
              <p
                className="text-editorial-muted mt-7 font-light"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.profilesIntro}
              </p>
            </div>

            <div className="rv grid grid-cols-1 md:grid-cols-2 gap-px bg-editorial-sand-deep mt-16">
              {copy.profiles.map((p) => (
                <article
                  key={p.flag}
                  className="bg-editorial-mineral hover:bg-editorial-bone transition-colors duration-500 p-10 md:p-12"
                  data-testid={`profile-${p.flag}`}
                >
                  <span
                    className="text-editorial-bronze block mb-6"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.6rem', letterSpacing: '0.28em', fontWeight: 500 }}
                  >
                    {p.flag}
                  </span>
                  <h3
                    className="text-editorial-charcoal font-light mb-5"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.7rem', lineHeight: '1.2' }}
                  >
                    {p.title}
                  </h3>
                  <p
                    className="text-editorial-muted font-light"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.92' }}
                  >
                    {p.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* TECHNICAL REALITIES — between profiles and process */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-32 bg-editorial-sand/30">
          <div className="max-w-6xl mx-auto">
            <div className="rv max-w-3xl">
              <Eyebrow>{copy.realitiesEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.realitiesTitle}
              </h2>
              <p
                className="text-editorial-muted mt-7 font-light"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.realitiesSub}
              </p>
            </div>

            <div className="rv grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-editorial-sand-deep mt-14">
              {copy.realities.map((r) => (
                <article
                  key={r.label}
                  className="bg-editorial-mineral hover:bg-editorial-bone transition-colors duration-500 p-9 md:p-10"
                  data-testid={`reality-${r.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <p
                    className="text-editorial-bronze block mb-5"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 500 }}
                  >
                    {r.label}
                  </p>
                  <h3
                    className="text-editorial-charcoal font-light mb-4"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.45rem', lineHeight: '1.22' }}
                  >
                    {r.title}
                  </h3>
                  <p
                    className="text-editorial-muted font-light"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', lineHeight: '1.88' }}
                  >
                    {r.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section id="process" className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-sand/50">
          <div className="max-w-5xl mx-auto">
            <div className="rv max-w-3xl">
              <Eyebrow>{copy.processEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.processTitle}
              </h2>
              <p
                className="text-editorial-muted mt-7 font-light"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.processSub}
              </p>
            </div>

            <div className="mt-14">
              {copy.process.map((s) => (
                <article
                  key={s.num}
                  className="rv grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-12 border-b"
                  style={{ borderColor: 'rgba(28,28,26,0.08)' }}
                  data-testid={`process-${s.num}`}
                >
                  <div className="col-span-12 md:col-span-2">
                    <span
                      className="text-editorial-sand-deep font-light"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '3.4rem', lineHeight: 1 }}
                    >
                      {s.num}
                    </span>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <h3
                      className="text-editorial-charcoal font-normal"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.45rem', lineHeight: '1.18' }}
                    >
                      {s.label}
                    </h3>
                  </div>
                  <div
                    className="col-span-12 md:col-span-7 text-editorial-muted font-light"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.92' }}
                  >
                    {s.body}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* EXPERTISE */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-6xl mx-auto">
            <div className="rv max-w-3xl">
              <Eyebrow dark>{copy.expertiseEyebrow}</Eyebrow>
              <h2
                className="text-editorial-bone font-light mt-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.expertiseTitle}
              </h2>
            </div>

            <div className="rv grid grid-cols-1 md:grid-cols-2 gap-px bg-editorial-bone/10 mt-14">
              {copy.expertise.map((x) => (
                <article
                  key={x.title}
                  className="bg-editorial-charcoal hover:bg-editorial-charcoal-soft transition-colors duration-500 p-8 md:p-10"
                >
                  <h3
                    className="text-editorial-bone font-light mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.35rem', lineHeight: '1.25' }}
                  >
                    {x.title}
                  </h3>
                  <p
                    className="text-editorial-bone/55 font-light"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', lineHeight: '1.9' }}
                  >
                    {x.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 bg-editorial-mineral">
          <div className="max-w-4xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.faqEyebrow}</Eyebrow>
              <h2
                className="text-editorial-charcoal font-light mt-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
              >
                {copy.faqTitle}
              </h2>
            </div>

            <div className="mt-14">
              {copy.faq.map((f, idx) => {
                const open = openFaq === idx;
                return (
                  <div key={idx} className="rv border-b" style={{ borderColor: 'rgba(28,28,26,0.1)' }}>
                    <button
                      onClick={() => setOpenFaq(open ? null : idx)}
                      className="w-full flex items-start justify-between gap-6 py-7 text-left group"
                      data-testid={`faq-${idx}`}
                      aria-expanded={open}
                    >
                      <h3
                        className="text-editorial-charcoal group-hover:text-editorial-bronze transition-colors font-normal"
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.35rem', lineHeight: '1.4' }}
                      >
                        {f.q}
                      </h3>
                      <span
                        className={`flex-shrink-0 text-editorial-bronze mt-1 transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', fontWeight: 300, lineHeight: 1 }}
                        aria-hidden="true"
                      >
                        +
                      </span>
                    </button>
                    <div
                      className="overflow-hidden transition-all duration-500"
                      style={{ maxHeight: open ? '500px' : '0', opacity: open ? 1 : 0 }}
                    >
                      <p
                        className="text-editorial-muted font-light pb-7 pr-12 max-w-3xl"
                        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.98rem', lineHeight: '1.92' }}
                      >
                        {f.a}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CLOSING */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-24 md:py-36 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rv">
              <h2
                className="text-editorial-bone font-light mb-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.6vw, 3.2rem)', lineHeight: '1.12' }}
              >
                {copy.closingTitle}
              </h2>
              <p
                className="text-editorial-bone/55 max-w-2xl mx-auto font-light mb-12"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.closingBody}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 sm:items-center sm:justify-center">
                <a
                  href={buildWhatsAppUrl('international', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    borderRadius: 0,
                  }}
                  data-testid="intl-closing-cta-whatsapp"
                >
                  {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
                </a>
                <Link
                  to={localizedPath('/contact')}
                  className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  data-testid="intl-closing-cta"
                >
                  {language === 'pt' ? 'Ou enviar pedido privado' : 'Or send a private enquiry'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default InternationalPage;
